import { Box, Checkbox, FormControlLabel, Grid } from "@mui/material";
import { Form, Formik } from "formik";
import { useEffect, useRef, useState } from "react";
import * as Yup from "yup";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import CTypography from "src/components/CTypography";
import FormikControl from "src/components/formik/FormikControl";
import { RoutePaths } from "src/constants";
import { ContainedButton, OutlinedButton } from "src/components/button-group";
import { AppDispatch } from "src/store";
import { selectLeadDetails } from "src/slices/leads/selectors";
import { ICreateUpdateLeadForm, ICreateUpdateleadPayload } from "../type";
import { createUpdateLead, fetchLeadDetailsById } from "src/slices/leads/thunk";
import { ILead } from "src/slices/leads/type";
import { format } from "date-fns";
import { DataGrid, GridSelectionModel } from "@mui/x-data-grid";
import { selectLocations } from "src/slices/configuration/selectors";
import { fetchLocations } from "src/slices/configuration/thunks";

export function CreateUpdateLead() {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  const { leadId } = useParams();
  const leadDetails = useSelector(selectLeadDetails);
  const locations = useSelector(selectLocations);

  const formikRef = useRef(null);
  const isCreateMode = !leadId;

  // console.log(formikRef?.current?.errors);

  const [addressCols] = useState([
    { field: "address", headerName: "Address", width: 400, editable: false },
    {
      field: "googleLocation",
      headerName: "Google Location",
      width: 400,
      editable: false,
    },
  ]);
  const [addressRows, setAddressRows] = useState([]);
  const [addressSelectionModel, setAddressSelectionModel] =
    useState<GridSelectionModel>([]);

  // Form Inital Values
  const formInitialValue: ICreateUpdateLeadForm = {
    leadFullName: "",
    emailAddress: "",
    contactNumber: "",
    alternateNumber: "",
    gender: "",
    leadChannel: "",
    isCustomer: false,
    dateOfBirth: undefined,
    address: "",
    addressLocation: "",
    branchName: "",
    branchId: 0,
  };

  // Form Fields Validation
  const validationSchemaModel = Yup.object().shape({
    contactNumber: Yup.string().required("Contact Number is Requied"),
    leadChannel: Yup.string().required("You must select a channel"),
    /*    address: Yup.string().test(
             "required Address",
             "Please add at least one address",
             (value) => addressRows.length > 0
           ), */
  });

  // Click Events on lead Submit Button
  const onClickSubmit = async (values: ICreateUpdateLeadForm) => {
    debugger;
    const lead = {
      leadId: leadId ? leadId : null,
      name: values.leadFullName === "" ? null : values.leadFullName,
      email: values.emailAddress === "" ? null : values.emailAddress,
      primaryNumber: values.contactNumber,
      secondaryNumber:
        values.alternateNumber === "" ? null : values.alternateNumber,
      gender:
        values.gender.toUpperCase() === "" ? null : values.gender.toUpperCase(),
      leadChannel: values.leadChannel.toLowerCase(),
      isCustomer: values.isCustomer,
      dob:
        values.dateOfBirth === undefined
          ? Date()
          : format(values.dateOfBirth, "yyyy-MM-dd"),
      branchId: values.branchId,
      addresses: addressRows.map((addressData) => ({
        address: addressData.address,
        googleLocation:
          addressData.googleLocation === "" ? null : addressData.googleLocation,
      })),
    } as ICreateUpdateleadPayload;

    dispatch(createUpdateLead(lead)).then((result: any) => {
      if (!result.payload.error) {
        navigate(RoutePaths.LEAD_LISTING);
      }
    });
  };

  // If lead Update Screen
  useEffect(() => {
    if (leadId) {
      dispatch(fetchLeadDetailsById(leadId));
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (leadId) {
      // console.log("lead id " + leadId);
      if (leadDetails) {
        loadLeadData(leadDetails);
        // console.log("lead details " + leadDetails);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadDetails]);

  const loadLeadData = async (lead: ILead) => {
    const { setFieldValue } = formikRef.current;

    setFieldValue("leadFullName", lead.name);
    setFieldValue("gender", lead.gender || "");
    setFieldValue("contactNumber", lead.primaryNumber);
    setFieldValue("alternateNumber", lead.secondaryNumber);
    setFieldValue("emailAddress", lead.email);
    setFieldValue("isCustomer", lead.isCustomer);
    setFieldValue("leadChannel", lead.leadChannel);
    // setFieldValue("branchName", lead.branchName || "");
    setFieldValue("branchId", lead.branchId || "");
  };

  function setFormValues(arg0: (prev: any) => any): void {
    throw new Error("Function not implemented.");
  }
  useEffect(() => {
    dispatch(fetchLocations());
  }, [dispatch]);

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Formik
          initialValues={formInitialValue}
          validationSchema={!leadId && validationSchemaModel}
          innerRef={formikRef}
          onSubmit={(values) => {
            onClickSubmit(values);
          }}
        >
          {({ values, touched, errors, setFieldValue, getFieldProps }) => (
            <Form id="Createlead">
              {/* Lead Information */}
              <Grid container spacing={1} pb={3}>
                <Grid item xs={12}>
                  <CTypography fontWeight="bold">
                    Lead Personal Information
                  </CTypography>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <FormikControl
                    control="InputField"
                    name="leadFullName"
                    label={"Lead Full Name"}
                    error={Boolean(touched.leadFullName && errors.leadFullName)}
                    helperText={touched.leadFullName && errors.leadFullName}
                    {...getFieldProps("leadFullName")}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <FormikControl
                    control="InputField"
                    name="emailAddress"
                    label="Email Address"
                    error={Boolean(touched.emailAddress && errors.emailAddress)}
                    helperText={touched.emailAddress && errors.emailAddress}
                    {...getFieldProps("emailAddress")}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <FormikControl
                    control="SelectField"
                    name="gender"
                    label="Gender"
                    options={[
                      {
                        label: "Male",
                        value: "M",
                      },
                      {
                        label: "Female",
                        value: "F",
                      },
                    ]}
                    error={Boolean(touched.gender && errors.gender)}
                    helperText={touched.gender && errors.gender}
                    {...getFieldProps("gender")}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <FormikControl
                    control="SelectField"
                    required
                    name="leadChannel"
                    label="Lead Channel"
                    options={[
                      {
                        label: "Whatsapp",
                        value: "whatsapp",
                      },
                      {
                        label: "Website",
                        value: "website",
                      },
                      {
                        label: "Walk In Customer",
                        value: "walk in customer",
                      },
                      {
                        label: "Direct Calls",
                        value: "direct calls",
                      },
                    ]}
                    error={Boolean(touched.leadChannel && errors.leadChannel)}
                    helperText={touched.leadChannel && errors.leadChannel}
                    {...getFieldProps("leadChannel")}
                  />
                </Grid>
              </Grid>

              {/* Contact Information */}
              <Grid container spacing={1} pb={3}>
                <Grid item xs={12}>
                  <CTypography fontWeight="bold">
                    Lead Contact Information
                  </CTypography>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <FormikControl
                    control="InputField"
                    required
                    name="contactNumber"
                    label="Contact Number"
                    error={Boolean(
                      touched.contactNumber && errors.contactNumber
                    )}
                    helperText={touched.contactNumber && errors.contactNumber}
                    {...getFieldProps("contactNumber")}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <FormikControl
                    control="InputField"
                    name="alternateNumber"
                    label="Alternate Number"
                    error={Boolean(
                      touched.alternateNumber && errors.alternateNumber
                    )}
                    helperText={
                      touched.alternateNumber && errors.alternateNumber
                    }
                    {...getFieldProps("alternateNumber")}
                  />
                </Grid>
              </Grid>

              {/* Lead Branch Imformation */}
              <Grid container spacing={1} pb={3}>
                <Grid item xs={12}>
                  <CTypography fontWeight="bold">
                    Lead Branch Information
                  </CTypography>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <FormikControl
                    control="SelectField"
                    name="branchId"
                    label="Branch Name"
                    options={locations.map((service) => ({
                      value: service.id,
                      label: service.name,
                    }))}
                    handleChange={(e: any) => {
                      // setFieldValue("branchName", e.target.value);
                      setFieldValue("branchId", e.target.value);
                    }}
                    error={Boolean(touched.branchId && errors.branchId)}
                    helperText={touched.branchId && errors.branchId}
                    {...getFieldProps("branchId")}
                  />
                </Grid>
              </Grid>

              {/* isCustomer? */}
              <Grid container spacing={1} pb={3}>
                <Grid item>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={values.isCustomer}
                        onChange={(e) =>
                          setFormValues((prev) => ({
                            ...prev,
                            isCustomer: e.target.checked,
                          }))
                        }
                        disabled={isCreateMode}
                        name="isCustomer"
                        {...getFieldProps("isCustomer")}
                      />
                    }
                    label="Is Customer?"
                  />
                </Grid>
              </Grid>
              {/* Customer Personal Information */}
              {values.isCustomer && (
                <Grid container spacing={1} pb={3}>
                  <Grid item xs={12}>
                    <CTypography fontWeight="bold">
                      Customer Personal Information
                    </CTypography>
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <FormikControl
                      control="CalendarTime"
                      label="Date of Birth"
                      dateOrTimeOnly="date"
                      name="dateOfBirth"
                      disableFuture
                      error={Boolean(touched.dateOfBirth && errors.dateOfBirth)}
                      helperText={touched.dateOfBirth && errors.dateOfBirth}
                      {...getFieldProps("dateOfBirth")}
                    />
                  </Grid>
                </Grid>
              )}
              {/* Branch Imformation */}
              {values.isCustomer && (
                <Grid container spacing={1} pb={3}>
                  <Grid item xs={12}>
                    <CTypography fontWeight="bold">
                      Customer Branch Information
                    </CTypography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormikControl
                      control="SelectField"
                      name="branchId"
                      label="Branch Name"
                      options={locations.map((service) => ({
                        value: service.id,
                        label: service.name,
                      }))}
                      handleChange={(e: any) => {
                        setFieldValue("branchId", e.target.value);
                      }}
                      error={Boolean(touched.branchId && errors.branchId)}
                      helperText={touched.branchId && errors.branchId}
                      {...getFieldProps("branchId")}
                    />
                  </Grid>
                </Grid>
              )}
              {/* Customer Contact Information */}
              {values.isCustomer && (
                <Grid container spacing={1} pb={3}>
                  <Grid item xs={12}>
                    <CTypography fontWeight="bold">
                      Customer Contact Information
                    </CTypography>
                  </Grid>

                  <Grid container spacing={1} item xs={12}>
                    <Grid item xs={12} sm={8}>
                      <FormikControl
                        control="InputField"
                        name="address"
                        label="Address"
                        error={Boolean(touched.address && errors.address)}
                        helperText={touched.address && errors.address}
                        {...getFieldProps("address")}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <FormikControl
                        control="InputField"
                        name="addressLocation"
                        label="Address Google Location"
                        error={Boolean(
                          touched.addressLocation && errors.addressLocation
                        )}
                        helperText={
                          touched.addressLocation && errors.addressLocation
                        }
                        {...getFieldProps("addressLocation")}
                      />
                    </Grid>
                  </Grid>

                  <Grid item xs={12}>
                    <Grid container spacing={3}>
                      <Grid item>
                        <OutlinedButton
                          size="small"
                          onClick={() => {
                            if (values.address.length === 0) return;
                            setAddressRows((rows) => [
                              ...rows,
                              {
                                id: rows.length,
                                address: values.address,
                                googleLocation: values.addressLocation,
                              },
                            ]);
                            setFieldValue("address", "");
                            setFieldValue("addressLocation", "");
                          }}
                        >
                          Add Address
                        </OutlinedButton>
                      </Grid>

                      <Grid item>
                        <OutlinedButton
                          size="small"
                          disabled={addressSelectionModel.length <= 0}
                          onClick={() => {
                            setAddressRows(
                              addressRows.filter(
                                (item) =>
                                  !addressSelectionModel.includes(item.id)
                              )
                            );
                          }}
                        >
                          Remove Address
                        </OutlinedButton>
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid item xs={12}>
                    <Box sx={{ height: 200, width: 1 }}>
                      <DataGrid
                        disableColumnFilter
                        disableColumnSelector
                        disableDensitySelector
                        hideFooter
                        checkboxSelection
                        onSelectionModelChange={(newSelectionModel) => {
                          setAddressSelectionModel(newSelectionModel);
                        }}
                        selectionModel={addressSelectionModel}
                        columns={addressCols}
                        rows={addressRows}
                      />
                    </Box>
                  </Grid>
                </Grid>
              )}

              {/* SUBMIT FORM */}
              <Grid container spacing={1}>
                <Grid item>
                  <ContainedButton type="submit">SUBMIT</ContainedButton>
                </Grid>
                <Grid item>
                  <OutlinedButton
                    onClick={() => {
                      navigate(-1);
                    }}
                  >
                    CANCEL
                  </OutlinedButton>
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      </Grid>
    </Grid>
  );
}
